// =============================================================================
// app/src/lib/rascunhoVenda.ts — o atendimento não se perde ao trocar de aba.
//
// Pedido do dono (19/08/2026): "se meu vendedor está realizando uma venda e
// vai para outra aba, não apague o que está vendendo".
//
// POR QUE O CARRINHO DO BANCO NÃO BASTAVA. Desde a migração 026 o carrinho é
// FATO no servidor: cada item é uma reserva de 30 min, e a tela o reconstrói
// depois de um F5. Só que a reserva guarda a PEÇA — e o atendimento é mais que
// isso. Não voltavam:
//
//   * a PEÇA DIVERSA (o coringa não tem reserva: é linha digitada na hora);
//   * o preço combinado, quando o vendedor mudou o da tabela;
//   * o desconto, o nome do cliente, a observação, o custo digitado da diversa.
//
// Ou seja: o vendedor ia à ficha da peça conferir uma aplicação, voltava, e o
// que ele tinha DIGITADO havia sumido — a peça continuava lá, o trabalho não.
//
// A SOLUÇÃO é rascunho no próprio aparelho: barato, instantâneo e sem inventar
// estado novo no banco (o carrinho de verdade continua sendo a reserva). Vale
// por 12 horas — atendimento de balcão não atravessa o dia, e rascunho velho
// reaparecendo no dia seguinte confundiria mais que ajudaria.
// =============================================================================

/** O que o rascunho guarda. Espelha o estado da tela de Venda que é DIGITADO
 *  (o resto — estoque, preço vigente — se relê do banco, e deve mesmo). */
export interface RascunhoVenda {
  itens: Array<{
    product_id: string;
    sku: string;
    descricao: string;
    quantidade: number;
    preco_unit: number;
    /** Identidade da LINHA na peça diversa (041). */
    linha?: string;
    descricao_avulsa?: string;
    custo_avulso?: number | null;
    /** 073 — o carro próprio da linha, quando ela tem um. */
    veiculo_montadora?: string;
    veiculo_modelo?: string;
    /** O que foi digitado no carro da linha (08/10/2026). */
    veiculo_texto?: string;
  }>;
  descontoTexto: string;
  descontoPctTexto: string;
  nomeCliente: string;
  clienteId: string | null;
  observacao: string;
  // A ficha da COTAÇÃO (049) também é digitada, e some junto se não vier aqui
  // — o dono pediu os dois: "quando estou orçando, não gostaria que ao abrir
  // outras abas o orçamento sumisse".
  contatoCliente?: string;
  veiculoOrc?: string;
  obsOrc?: string;
  canalOrc?: 'balcao' | 'whatsapp' | 'telefone';
  /** 073 — o carro da VENDA. Digitado como o resto: some junto se não vier.
   *  Guardado DESMONTADO, como vai para o banco, e — desde 08/10/2026, quando
   *  virou campo de texto — também a FRASE que o vendedor digitou
   *  (`carroTexto`): é ela que o campo mostra na volta. Rascunho de antes
   *  disso não tem a frase; o carro volta pelas partes (lib/carro.ts,
   *  carroDoRascunho). */
  carroMontadora?: string;
  carroModelo?: string;
  carroAno?: string;
  carroVeiculoId?: string | null;
  carroTexto?: string;
  /** Quando foi salvo — o que decide se ainda vale. */
  salvo_em: number;
}

export const VALIDADE_MS = 12 * 60 * 60 * 1000;

/** Uma chave por PESSOA: dois vendedores no mesmo computador (o do balcão é
 *  compartilhado) não podem herdar o atendimento um do outro. */
export function chaveDoRascunho(perfilId: string | null | undefined): string {
  return `venda-rascunho-v1:${perfilId ?? 'anonimo'}`;
}

/** Rascunho vazio não se guarda: carrinho sem item nenhum e sem nada digitado
 *  só faria a tela "restaurar" o nada e piscar. */
export function vaziO(r: Omit<RascunhoVenda, 'salvo_em'>): boolean {
  return r.itens.length === 0
    && !r.descontoTexto.trim() && !r.descontoPctTexto.trim()
    && !r.nomeCliente.trim() && !r.observacao.trim();
}

/**
 * O armazém entra por parâmetro para estas funções continuarem PURAS e
 * testáveis sem navegador — o mesmo motivo pelo qual `urlDaImagem` recebe a
 * base em vez de ler do import.meta. Em produção o padrão é o localStorage;
 * onde ele não existe (teste em node, modo privado que bloqueia), vira um
 * armazém que não guarda nada e a tela segue funcionando.
 */
export interface Armazem {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
}

const SEM_ARMAZEM: Armazem = {
  getItem: () => null, setItem: () => {}, removeItem: () => {},
};

export function armazemPadrao(): Armazem {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : SEM_ARMAZEM;
  } catch {
    return SEM_ARMAZEM;
  }
}

export function salvarRascunho(
  chave: string, r: Omit<RascunhoVenda, 'salvo_em'>,
  store: Armazem = armazemPadrao(), agora = Date.now(),
): void {
  try {
    if (vaziO(r)) { store.removeItem(chave); return; }
    store.setItem(chave, JSON.stringify({ ...r, salvo_em: agora }));
  } catch { /* aparelho sem espaço: o atendimento segue, só não é guardado */ }
}

export function lerRascunho(
  chave: string, agora = Date.now(), store: Armazem = armazemPadrao(),
): RascunhoVenda | null {
  try {
    const bruto = store.getItem(chave);
    if (!bruto) return null;
    const r = JSON.parse(bruto) as RascunhoVenda;
    if (!Array.isArray(r.itens) || typeof r.salvo_em !== 'number') return null;
    if (agora - r.salvo_em > VALIDADE_MS) { store.removeItem(chave); return null; }
    return r;
  } catch {
    return null;
  }
}

export function limparRascunho(chave: string, store: Armazem = armazemPadrao()): void {
  try { store.removeItem(chave); } catch { /* nada a fazer */ }
}

/**
 * A volta à Venda com a lista JÁ cheia pelo rascunho (06/10/2026). O rascunho
 * enche as linhas com disponível 0 (estoque é do banco, não do aparelho), e a
 * reserva lida logo depois (meu_carrinho) desistia por ver a lista cheia: toda
 * linha ficava "acima do estoque", o que era falso — bastava ir ao mapa grande
 * e voltar.
 *
 * Agora a leitura da reserva ESCREVE SÓ o `disponivel`, peça a peça. Quantidade,
 * preço, a linha de peça diversa (que nunca reservou) e o que foi digitado não
 * são tocados: é a trava de "tocado" do CLAUDE.md. Nada mudou → devolve a MESMA
 * lista, e o React não redesenha à toa.
 */
export function mesclarDisponivel<T extends { product_id: string; disponivel: number; linha?: string }>(
  itens: readonly T[],
  doBanco: ReadonlyArray<{ product_id: string; disponivel: number }>,
): T[] {
  const porPeca = new Map(doBanco.map((l) => [l.product_id, Number(l.disponivel)]));
  let mudou = false;
  const saida = itens.map((i) => {
    if (i.linha) return i;
    const d = porPeca.get(i.product_id);
    if (d === undefined || !Number.isFinite(d) || d === i.disponivel) return i;
    mudou = true;
    return { ...i, disponivel: d };
  });
  return mudou ? saida : (itens as T[]);
}
